import React, { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Package,
  Search,
  Eye,
  Calendar,
  User,
  CreditCard,
  Truck,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  ChevronDown,
  RefreshCw,
  Download,
  Copy,
} from "lucide-react";
import toast from "react-hot-toast";
import {
  getAdminOrders,
  updateOrderStatus,
  updateOrderShipping,
  exportOrders,
  SITE_BASE_URL,
} from "../../config/api";
import ShippingModal from "./ShippingModal";
import { getImageUrl } from "../../utils/helpers";
import { openProductById } from "../../utils/productNavigation";

const OrderManagement = () => {
  const [searchParams] = useSearchParams();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(
    () => searchParams.get("search") || "",
  );
  const [statusFilter, setStatusFilter] = useState("all");
  const [paymentFilter, setPaymentFilter] = useState("all");
  const [startDate, setStartDate] = useState(() => {
    return localStorage.getItem("orderStartDate") || "";
  });
  const [endDate, setEndDate] = useState(() => {
    return localStorage.getItem("orderEndDate") || "";
  });
  const [exporting, setExporting] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [showShippingModal, setShowShippingModal] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
  });

  const orderStatuses = [
    { value: "pending", label: "Pending", color: "yellow", icon: Clock },
    {
      value: "paid",
      label: "Payment Confirmed",
      color: "green",
      icon: CheckCircle,
    },
    {
      value: "confirmed",
      label: "Confirmed",
      color: "blue",
      icon: CheckCircle,
    },
    {
      value: "processing",
      label: "Processing",
      color: "indigo",
      icon: Package,
    },
    { value: "shipped", label: "Shipped", color: "purple", icon: Truck },
    {
      value: "delivered",
      label: "Delivered",
      color: "green",
      icon: CheckCircle,
    },
    { value: "cancelled", label: "Cancelled", color: "red", icon: XCircle },
    { value: "refunded", label: "Refunded", color: "gray", icon: AlertCircle },
  ];

  const paymentStatuses = [
    { value: "pending", label: "Pending", color: "yellow" },
    { value: "completed", label: "Completed", color: "green" },
    { value: "failed", label: "Failed", color: "red" },
    { value: "refunded", label: "Refunded", color: "gray" },
  ];

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        ...(statusFilter !== "all" && { status: statusFilter }),
        ...(paymentFilter !== "all" && { payment_status: paymentFilter }),
        ...(searchTerm && { search: searchTerm }),
        ...(startDate && { start_date: startDate }),
        ...(endDate && { end_date: endDate }),
      });

      const response = await getAdminOrders(Object.fromEntries(params));
      setOrders(response.data || []);
      setPagination((prev) => response.pagination || prev);
    } catch (error) {
      console.error("Error fetching orders:", error);
      toast.error("Failed to fetch orders");
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [
    pagination.page,
    pagination.limit,
    statusFilter,
    paymentFilter,
    searchTerm,
    startDate,
    endDate,
  ]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const handleUpdateOrderStatus = async (orderId, newStatus) => {
    // If status is 'shipped', show shipping modal instead
    if (newStatus === "shipped") {
      const order = orders.find((o) => o.id === orderId);
      setSelectedOrder(order);
      setShowShippingModal(true);
      return;
    }

    try {
      setUpdatingStatus(true);
      await updateOrderStatus(orderId, newStatus);

      // Update orders list
      setOrders(
        orders.map((order) =>
          order.id === orderId ? { ...order, status: newStatus } : order
        )
      );

      // Update selected order if it's the one being updated
      if (selectedOrder && selectedOrder.id === orderId) {
        setSelectedOrder({ ...selectedOrder, status: newStatus });
      }

      toast.success("Order status updated successfully");
    } catch (error) {
      console.error("Error updating order status:", error);
      const message =
        error.response?.data?.message || "Failed to update order status";
      toast.error(message);
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleShippingSubmit = async (shippingData) => {
    try {
      setUpdatingStatus(true);
      const response = await updateOrderShipping(
        selectedOrder.id,
        shippingData
      );

      // Update orders list with the new order data
      setOrders(
        orders.map((order) =>
          order.id === selectedOrder.id ? { ...order, ...response.data } : order
        )
      );

      // Update selected order if it's the one being updated
      if (selectedOrder) {
        setSelectedOrder({ ...selectedOrder, ...response.data });
      }

      setShowShippingModal(false);
      toast.success("Order marked as shipped successfully");
    } catch (error) {
      console.error("Error updating shipping information:", error);
      toast.error("Failed to update shipping information");
    } finally {
      setUpdatingStatus(false);
    }
  };

  const getStatusColor = (status) => {
    const statusObj = orderStatuses.find((s) => s.value === status);
    return statusObj ? statusObj.color : "gray";
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const copyTrackingCode = async (token) => {
    if (!token) return;
    try {
      await navigator.clipboard.writeText(token);
      toast.success("Tracking code copied");
    } catch {
      toast.error("Could not copy tracking code");
    }
  };

  const copyTrackingLink = async (token) => {
    if (!token) return;
    const url = `${SITE_BASE_URL.replace(/\/+$/, "")}/track/${token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Tracking link copied");
    } catch {
      toast.error("Could not copy tracking link");
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      
      // Build export parameters
      const params = {
        ...(statusFilter !== "all" && { status: statusFilter }),
        ...(paymentFilter !== "all" && { payment_status: paymentFilter }),
        ...(startDate && { start_date: startDate }),
        ...(endDate && { end_date: endDate }),
      };

      const blob = await exportOrders(params);
      
      // Check if blob is actually an error response (JSON)
      if (blob.type === "application/json") {
        const text = await blob.text();
        const errorData = JSON.parse(text);
        toast.error(errorData.message || "Failed to export orders");
        return;
      }
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      
      // Generate filename with date range if provided
      const dateStr = startDate && endDate 
        ? `${startDate}_to_${endDate}`
        : new Date().toISOString().split("T")[0];
      link.download = `orders-export-${dateStr}.xlsx`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success("Orders exported successfully!");
    } catch (error) {
      console.error("Error exporting orders:", error);
      // Error toast is already shown by the API interceptor
      if (!error.response) {
        toast.error("Failed to export orders. Please try again.");
      }
    } finally {
      setExporting(false);
    }
  };

  const filteredOrders = orders.filter((order) => {
    if (!order) return false;
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch =
      (order.order_number &&
        order.order_number.toLowerCase().includes(searchLower)) ||
      (order.customer_details?.email &&
        order.customer_details.email.toLowerCase().includes(searchLower)) ||
      (order.customer_details?.firstName &&
        order.customer_details.firstName.toLowerCase().includes(searchLower)) ||
      (order.customer_details?.lastName &&
        order.customer_details.lastName.toLowerCase().includes(searchLower)) ||
      (order.order_token &&
        order.order_token.toLowerCase().includes(searchLower));

    return matchesSearch;
  });

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, staggerChildren: 0.1 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: { opacity: 1, x: 0 },
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="max-w-7xl mx-auto"
      >
        {/* Header */}
        <motion.div variants={itemVariants} className="mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center">
                <Package className="mr-3 text-blue-600" />
                Order Management
              </h1>
              <p className="text-gray-600 mt-2">
                Manage and track all customer orders
              </p>
            </div>
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
              <button
                onClick={fetchOrders}
                disabled={loading}
                className="flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {loading ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-2" />
                )}
                Refresh
              </button>
              <button
                onClick={handleExport}
                disabled={exporting}
                className="flex items-center justify-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {exporting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Exporting...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Export
                  </>
                )}
              </button>
            </div>
          </div>
        </motion.div>

        {/* Filters */}
        <motion.div
          variants={itemVariants}
          className="bg-white rounded-xl shadow-sm p-4 sm:p-6 mb-6"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search orders, tracking code, customer..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Status Filter */}
            <div className="relative">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white"
              >
                <option value="all">All Statuses</option>
                {orderStatuses.map((status) => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
            </div>

            {/* Payment Filter */}
            <div className="relative">
              <select
                value={paymentFilter}
                onChange={(e) => setPaymentFilter(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white"
              >
                <option value="all">All Payments</option>
                {paymentStatuses.map((status) => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
            </div>

            {/* Start Date */}
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="date"
                value={startDate}
                onChange={(e) => {
                  const value = e.target.value;
                  setStartDate(value);
                  localStorage.setItem("orderStartDate", value);
                }}
                placeholder="Start Date"
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* End Date */}
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="date"
                value={endDate}
                onChange={(e) => {
                  const value = e.target.value;
                  setEndDate(value);
                  localStorage.setItem("orderEndDate", value);
                }}
                placeholder="End Date"
                min={startDate}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        </motion.div>

        {/* Orders Table - Mobile Card View */}
        <motion.div
          variants={itemVariants}
          className="bg-white rounded-xl shadow-sm overflow-hidden"
        >
          {/* Desktop Table View */}
          <div className="hidden lg:block overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Order Details
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Payment
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center">
                      <div className="flex items-center justify-center">
                        <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mr-2" />
                        Loading orders...
                      </div>
                    </td>
                  </tr>
                ) : filteredOrders.length === 0 ? (
                  <tr>
                    <td
                      colSpan="7"
                      className="px-6 py-12 text-center text-gray-500"
                    >
                      No orders found
                    </td>
                  </tr>
                ) : (
                  filteredOrders.map((order) => (
                    <tr
                      key={order.id}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-6 py-4">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {order.order_number}
                          </div>
                          <div className="text-sm text-gray-500">
                            {order.order_items?.length || 0} items
                          </div>
                          {order.order_token && (
                            <div className="mt-1 flex items-center gap-1 text-xs text-gray-500">
                              <span>Track:</span>
                              <code className="font-mono text-gray-700">
                                {order.order_token}
                              </code>
                              <button
                                type="button"
                                onClick={() => copyTrackingCode(order.order_token)}
                                className="text-gray-400 hover:text-blue-600 transition-colors"
                                title="Copy tracking code"
                              >
                                <Copy className="w-3 h-3" />
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                            <User className="w-5 h-5 text-gray-500" />
                          </div>
                          <div className="ml-3">
                            <div className="text-sm font-medium text-gray-900">
                              {order.customer_details?.firstName}{" "}
                              {order.customer_details?.lastName}
                            </div>
                            <div className="text-sm text-gray-500">
                              {order.customer_details?.email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">
                          {formatCurrency(order.total_price)}
                        </div>
                        <div className="text-sm text-gray-500">
                          via {order.payment_method}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <select
                          value={order.status}
                          onChange={(e) =>
                            handleUpdateOrderStatus(order.id, e.target.value)
                          }
                          disabled={updatingStatus}
                          className={`text-sm font-medium px-3 py-1 rounded-full border focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 bg-${getStatusColor(
                            order.status
                          )}-100 text-${getStatusColor(order.status)}-800`}
                        >
                          {orderStatuses.map((status) => (
                            <option key={status.value} value={status.value}>
                              {status.label}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${getStatusColor(
                            order.payment_status
                          )}-100 text-${getStatusColor(
                            order.payment_status
                          )}-800`}
                        >
                          <CreditCard className="w-3 h-3 mr-1" />
                          {order.payment_status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {formatDate(order.created_at)}
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => {
                            setSelectedOrder(order);
                            setShowOrderModal(true);
                          }}
                          className="text-blue-600 hover:text-blue-900 transition-colors"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="lg:hidden">
            {loading ? (
              <div className="p-6 text-center">
                <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                <p className="text-gray-600">Loading orders...</p>
              </div>
            ) : filteredOrders.length === 0 ? (
              <div className="p-6 text-center">
                <Package className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                <p className="text-gray-500">No orders found</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {filteredOrders.map((order) => (
                  <div key={order.id} className="p-4 hover:bg-gray-50">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                            <User className="w-4 h-4 text-gray-500" />
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {order.customer_details?.firstName}{" "}
                              {order.customer_details?.lastName}
                            </div>
                            <div className="text-xs text-gray-500">
                              {order.customer_details?.email}
                            </div>
                          </div>
                        </div>
                        <div className="text-sm font-medium text-gray-900">
                          {order.order_number}
                        </div>
                        <div className="text-xs text-gray-500">
                          {order.order_items?.length || 0} items
                        </div>
                        {order.order_token && (
                          <div className="mt-1 flex items-center gap-1 text-xs text-gray-500">
                            <span>Track:</span>
                            <code className="font-mono text-gray-700">
                              {order.order_token}
                            </code>
                            <button
                              type="button"
                              onClick={() => copyTrackingCode(order.order_token)}
                              className="text-gray-400 hover:text-blue-600 transition-colors"
                              title="Copy tracking code"
                            >
                              <Copy className="w-3 h-3" />
                            </button>
                          </div>
                        )}
                      </div>
                      <button
                        onClick={() => {
                          setSelectedOrder(order);
                          setShowOrderModal(true);
                        }}
                        className="text-blue-600 hover:text-blue-900 transition-colors p-1"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </div>

                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <span className="text-gray-500">Amount:</span>
                        <div className="font-medium text-gray-900">
                          {formatCurrency(order.total_price)}
                        </div>
                      </div>
                      <div>
                        <span className="text-gray-500">Date:</span>
                        <div className="text-gray-900">
                          {formatDate(order.created_at)}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between mt-3">
                      <select
                        value={order.status}
                        onChange={(e) =>
                          handleUpdateOrderStatus(order.id, e.target.value)
                        }
                        disabled={updatingStatus}
                        className={`text-xs font-medium px-2 py-1 rounded-full border focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 bg-${getStatusColor(
                          order.status
                        )}-100 text-${getStatusColor(order.status)}-800`}
                      >
                        {orderStatuses.map((status) => (
                          <option key={status.value} value={status.value}>
                            {status.label}
                          </option>
                        ))}
                      </select>

                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-${getStatusColor(
                          order.payment_status
                        )}-100 text-${getStatusColor(
                          order.payment_status
                        )}-800`}
                      >
                        <CreditCard className="w-3 h-3 mr-1" />
                        {order.payment_status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                <div className="text-sm text-gray-700 text-center sm:text-left">
                  Showing {(pagination.page - 1) * pagination.limit + 1} to{" "}
                  {Math.min(
                    pagination.page * pagination.limit,
                    pagination.total
                  )}{" "}
                  of {pagination.total} results
                </div>
                <div className="flex justify-center sm:justify-end space-x-2">
                  <button
                    onClick={() =>
                      setPagination((prev) => ({
                        ...prev,
                        page: prev.page - 1,
                      }))
                    }
                    disabled={pagination.page === 1}
                    className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() =>
                      setPagination((prev) => ({
                        ...prev,
                        page: prev.page + 1,
                      }))
                    }
                    disabled={pagination.page === pagination.pages}
                    className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </motion.div>

        {/* Order Detail Modal */}
        {showOrderModal && selectedOrder && (
          <OrderDetailModal
            order={selectedOrder}
            onClose={() => {
              setShowOrderModal(false);
              setSelectedOrder(null);
            }}
            onStatusUpdate={handleUpdateOrderStatus}
            orderStatuses={orderStatuses}
            formatCurrency={formatCurrency}
            formatDate={formatDate}
            copyTrackingCode={copyTrackingCode}
            copyTrackingLink={copyTrackingLink}
          />
        )}

        {/* Shipping Modal */}
        <ShippingModal
          isOpen={showShippingModal}
          onClose={() => {
            setShowShippingModal(false);
            setSelectedOrder(null);
          }}
          onSubmit={handleShippingSubmit}
          order={selectedOrder}
          isLoading={updatingStatus}
        />
      </motion.div>
    </div>
  );
};

// Order Detail Modal Component
const OrderDetailModal = ({
  order,
  onClose,
  onStatusUpdate,
  orderStatuses,
  formatCurrency,
  formatDate,
  copyTrackingCode,
  copyTrackingLink,
}) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="p-4 sm:p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
              Order Details - {order.order_number}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <XCircle className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="p-4 sm:p-6 space-y-6">
          {/* Order Summary */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            <div className="bg-blue-50 p-4 rounded-lg">
              <h3 className="font-semibold text-blue-900 mb-2">Order Status</h3>
              <select
                value={order.status}
                onChange={(e) => onStatusUpdate(order.id, e.target.value)}
                className="w-full px-3 py-2 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                {orderStatuses.map((status) => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <h3 className="font-semibold text-green-900 mb-2">
                Total Amount
              </h3>
              <p className="text-xl sm:text-2xl font-bold text-green-800">
                {formatCurrency(order.total_price)}
              </p>
              {order.coupon_code && Number(order.discount_amount) > 0 && (
                <p className="text-sm text-green-700 mt-1">
                  Coupon {order.coupon_code}: -{formatCurrency(order.discount_amount)}
                </p>
              )}
            </div>
            <div className="bg-purple-50 p-4 rounded-lg">
              <h3 className="font-semibold text-purple-900 mb-2">Order Date</h3>
              <p className="text-purple-800">{formatDate(order.created_at)}</p>
            </div>
          </div>

          {order.fulfillment_warning && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-amber-900">Fulfillment warning</p>
                <p className="text-sm text-amber-800">
                  {typeof order.fulfillment_warning === "string"
                    ? order.fulfillment_warning
                    : order.fulfillment_warning?.code || "Stock or fulfillment issue detected"}
                </p>
              </div>
            </div>
          )}

          {order.order_token && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 sm:p-6">
              <h3 className="font-semibold text-amber-900 mb-3">
                Customer Tracking Code
              </h3>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <code className="text-lg font-mono font-semibold text-amber-950">
                      {order.order_token}
                    </code>
                    <button
                      type="button"
                      onClick={() => copyTrackingCode(order.order_token)}
                      className="text-amber-700 hover:text-amber-900 transition-colors"
                      title="Copy tracking code"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                  <p className="text-sm text-amber-800 mt-2 break-all">
                    {SITE_BASE_URL.replace(/\/+$/, "")}/track/{order.order_token}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => copyTrackingLink(order.order_token)}
                    className="inline-flex items-center px-3 py-2 text-sm font-medium border border-amber-300 text-amber-900 rounded-lg hover:bg-amber-100 transition-colors"
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Copy Link
                  </button>
                  <Link
                    to={`/track/${order.order_token}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-3 py-2 text-sm font-medium bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors"
                  >
                    <Truck className="w-4 h-4 mr-2" />
                    Open Track Page
                  </Link>
                </div>
              </div>
            </div>
          )}

          {/* Customer Information */}
          <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6">
            <h3 className="font-semibold text-gray-900 mb-3">
              Customer Information
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Name</p>
                <p className="font-medium">
                  {order.customer_details?.firstName}{" "}
                  {order.customer_details?.lastName}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Email</p>
                <p className="font-medium">{order.customer_details?.email}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Phone</p>
                <p className="font-medium">{order.customer_details?.phone}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Payment Method</p>
                <p className="font-medium">{order.payment_method}</p>
              </div>
            </div>
          </div>

          {/* Shipping Address */}
          {order.shipping_address && (
            <div className="bg-gray-50 p-4 sm:p-6 rounded-lg">
              <h3 className="font-semibold text-gray-900 mb-3">
                Shipping Address
              </h3>
              <p className="text-gray-700">
                {order.shipping_address.address}
                <br />
                {order.shipping_address.city},{" "}
                {order.shipping_address.postalCode}
                <br />
                {order.shipping_address.country}
              </p>
            </div>
          )}

          {/* Shipping Tracking Information */}
          {(order.tracking_number || order.shipping_company) && (
            <div className="bg-blue-50 p-4 sm:p-6 rounded-lg">
              <h3 className="font-semibold text-blue-900 mb-3">
                Shipping Information
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {order.tracking_number && (
                  <div>
                    <p className="text-sm text-blue-600">Tracking Number</p>
                    <p className="font-medium text-blue-900">
                      {order.tracking_number}
                    </p>
                  </div>
                )}
                {order.shipping_company && (
                  <div>
                    <p className="text-sm text-blue-600">Carrier</p>
                    <p className="font-medium text-blue-900">
                      {order.shipping_company}
                    </p>
                  </div>
                )}
                {order.tracking_url && (
                  <div className="sm:col-span-2">
                    <a
                      href={order.tracking_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-700 underline"
                    >
                      Open tracking link
                    </a>
                  </div>
                )}
                {order.shipped_at && (
                  <div className="sm:col-span-2">
                    <p className="text-sm text-blue-600">Shipped On</p>
                    <p className="text-blue-900">
                      {formatDate(order.shipped_at)}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Order Items */}
          {order.order_items && order.order_items.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6">
              <h3 className="font-semibold text-gray-900 mb-3">Order Items</h3>
              <div className="space-y-3">
                {order.order_items.map((item, index) => (
                  <div
                    key={index}
                    className="flex items-center space-x-4 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => openProductById(item.product_id, item.variant_id)}
                  >
                    {item.image ? (
                      <img
                        src={getImageUrl(item.image)}
                        alt={item.name}
                        className="w-16 h-16 object-cover rounded-lg"
                        onError={(e) => {
                          console.error(
                            `❌ Image failed to load for ${item.name}:`,
                            {
                              originalSrc: item.image,
                              resolvedSrc: getImageUrl(item.image),
                              currentHost: window.location.hostname,
                            }
                          );
                          e.target.style.display = "none";
                        }}
                        onLoad={() => {
                          console.log(
                            `✅ Image loaded successfully for ${item.name}:`,
                            getImageUrl(item.image)
                          );
                        }}
                      />
                    ) : (
                      <div className="w-16 h-16 bg-gray-200 rounded-lg flex items-center justify-center">
                        <span className="text-sm text-gray-500 font-medium text-center px-1">
                          {item.name
                            .split(" ")
                            .slice(0, 2)
                            .map((word) => word.charAt(0))
                            .join("")
                            .toUpperCase()}
                        </span>
                      </div>
                    )}
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900 hover:text-blue-600 transition-colors">
                        {item.name}
                      </h4>
                      <p className="text-sm text-gray-600">
                        Quantity: {item.quantity}
                      </p>

                      {/* Variant Details */}
                      {item.variant_sku && (
                        <p className="text-sm text-blue-600 font-medium">
                          SKU: {item.variant_sku}
                        </p>
                      )}

                      {/* Variant Attributes */}
                      {item.variant_attributes &&
                        Object.keys(item.variant_attributes).length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {Object.entries(item.variant_attributes).map(
                              ([key, value]) => (
                                <span
                                  key={key}
                                  className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"
                                >
                                  {key}: {value}
                                </span>
                              )
                            )}
                          </div>
                        )}

                      <p className="text-xs text-gray-400 mt-1">
                        Click to view product →
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium">
                        {formatCurrency(item.price)}
                      </p>
                      <p className="text-sm text-gray-600">
                        Total: {formatCurrency(item.price * item.quantity)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Price Breakdown */}
          <div className="bg-gray-50 p-4 sm:p-6 rounded-lg">
            <h3 className="font-semibold text-gray-900 mb-3">
              Price Breakdown
            </h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span>Items Total:</span>
                <span>{formatCurrency(order.items_price || 0)}</span>
              </div>

              {Number(order.discount_amount) > 0 && (
                <div className="flex justify-between text-green-700">
                  <span>
                    Discount{order.coupon_code ? ` (${order.coupon_code})` : ""}
                  </span>
                  <span>-{formatCurrency(order.discount_amount)}</span>
                </div>
              )}

              <div className="flex justify-between">
                <span>Shipping:</span>
                <span>{formatCurrency(order.shipping_price || 0)}</span>
              </div>
              <div className="flex justify-between font-bold text-lg border-t pt-2">
                <span>Total:</span>
                <span>{formatCurrency(order.total_price)}</span>
              </div>
              <div className="text-center mt-2">
                <span className="text-xs text-green-600 font-medium bg-green-50 px-2 py-1 rounded-full">
                  Inclusive of all taxes
                </span>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default OrderManagement;
